import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1080;

function GraphicRecapVictory({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  detailTextColor = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageScale = null,
  dominantImageOpacity = 100,
  dateFormat = 'full',
}) {
  const {
    homeTeam,
    homeScore,
    awayTeam,
    awayScore,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);
  const resultLabel = isTie ? 'TIE GAME' : homeWon ? 'VICTORY' : 'FINAL';

  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const accentColor = accentBlockColor || teamColors.secondary;
  const scale = (dominantImageScale ?? 100) / 100;
  const scoreFontSize = heroFontSize ? Number(heroFontSize) : 280;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  return (
    <div
      id="graphic-recap"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1: Background photo */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 2a: Dominant image */}
      {dominantImage && (
        <img
          src={dominantImage}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: `${dominantImageY ?? 50}%`,
            left: `${dominantImageX ?? 50}%`,
            transform: `translate(-50%, -50%) scale(${scale})`,
            opacity: (dominantImageOpacity ?? 100) / 100,
            transformOrigin: 'center center',
            height: 'auto',
            width: '100%',
            maxWidth: 'none',
            overflow: 'visible',
            zIndex: 2,
          }}
        />
      )}

      {/* Layer 2b: Diagonal accent block (no dominant image) */}
      {!dominantImage && (
        <div
          style={{
            position: 'absolute',
            bottom: -320,
            right: -320,
            width: 960,
            height: 960,
            background: accentColor,
            transform: 'rotate(25deg)',
            zIndex: 2,
          }}
        />
      )}

      {/* Layer 3: Gradient scrim */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)',
          zIndex: 3,
        }}
      />

      {/* Layer 4: Result pill */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 60,
          zIndex: 4,
          background: teamColors.primary,
          padding: '20px 48px',
          fontFamily: heroFontFamily,
          fontSize: 72,
          color: '#ffffff',
          letterSpacing: 8,
          lineHeight: 1,
        }}
      >
        {resultLabel}
      </div>

      {/* Layer 5: Score row */}
      <div
        style={{
          position: 'absolute',
          top: 200,
          left: 60,
          zIndex: 5,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 20,
        }}
      >
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: scoreFontSize,
            color: '#ffffff',
            lineHeight: 1,
          }}
        >
          {homeScore || '0'}
        </div>
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: Math.round(scoreFontSize * 0.5),
            color: 'rgba(255,255,255,0.3)',
            lineHeight: 1.3,
          }}
        >
          -
        </div>
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: scoreFontSize,
            color: 'rgba(255,255,255,0.45)',
            lineHeight: 1,
          }}
        >
          {awayScore || '0'}
        </div>
      </div>

      {/* Layer 6: Thin accent rule */}
      <div
        style={{
          position: 'absolute',
          top: 600,
          left: 60,
          width: 160,
          height: 6,
          background: accentColor,
          zIndex: 6,
        }}
      />

      {/* Layer 7: Top performer / fallback team name */}
      <div
        style={{
          position: 'absolute',
          top: 630,
          left: 60,
          right: 60,
          zIndex: 7,
        }}
      >
        {topPerformer ? (
          <>
            <div
              style={{
                fontFamily: barlowFamily,
                fontSize: 32,
                color: accentColor,
                letterSpacing: 6,
                lineHeight: 1,
                marginBottom: 8,
              }}
            >
              PLAYER OF THE GAME
            </div>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 100,
                color: '#ffffff',
                lineHeight: 0.9,
                marginBottom: 12,
              }}
            >
              {topPerformer}
            </div>
            {topPerformerStats && (
              <div
                style={{
                  fontFamily: barlowFamily,
                  fontSize: 48,
                  color: detailTextColor || 'rgba(255,255,255,0.75)',
                  lineHeight: 1,
                }}
              >
                {topPerformerStats}
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              fontFamily: heroFontFamily,
              fontSize: 80,
              color: '#ffffff',
              lineHeight: 1,
            }}
          >
            {homeTeam || 'YOUR TEAM'}
          </div>
        )}
      </div>

      {/* Layer 8a: Logo row */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          zIndex: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        {homeTeamLogo && (
          <img
            src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
            alt={homeTeam || 'Home'}
            crossOrigin="anonymous"
            style={{ height: 64, width: 'auto' }}
          />
        )}
        <div
          style={{
            fontFamily: barlowFamily,
            fontSize: 32,
            color: '#ffffff',
            letterSpacing: 2,
            lineHeight: 1,
          }}
        >
          {homeTeam || 'YOUR TEAM'}
        </div>
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: 40,
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1,
            margin: '0 8px',
          }}
        >
          VS
        </div>
        {awayTeamLogoPreview && (
          <img
            src={awayTeamLogoPreview}
            alt={awayTeam || 'Away'}
            crossOrigin="anonymous"
            style={{ height: 64, width: 'auto' }}
          />
        )}
        <div
          style={{
            fontFamily: barlowFamily,
            fontSize: 32,
            color: '#ffffff',
            letterSpacing: 2,
            lineHeight: 1,
          }}
        >
          {awayTeam || 'OPPONENT'}
        </div>
      </div>

      {/* Layer 8b: Game date */}
      {gameDate && (
        <div
          style={{
            position: 'absolute',
            bottom: 60,
            right: 60,
            zIndex: 8,
            fontFamily: barlowFamily,
            fontSize: 36,
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: 2,
            lineHeight: 1,
          }}
        >
          {formatGameDate(gameDate, dateFormat)}
        </div>
      )}

      {/* Layer 9: Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 9,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicRecapVictory;
